import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { clevertap_script, fb_pixel, gtm_script, blitzlamaScript, verloop_script, microsoft_clarity } from './external_js';
import { VariablesService } from '@services/variables/variables.service';

const Scripts = {
  blitzlama: blitzlamaScript,
  card_validator: '/assets/js/card-validator.js',
  clevertap: clevertap_script,
  fb_pixel_script: fb_pixel,
  google_login: 'https://accounts.google.com/gsi/client',
  googlemaps: 'https://maps.googleapis.com/maps/api/js?key=AIzaSyDCF-b1N89za5IiNd95L27vx-XyFzUeoCE&callback&libraries=places',
  gtm: gtm_script,
  // hubble: 'https://assets.myhubble.money/web-sdk/hubble-store-sdk-0.1.2.min.js',
  jquery: 'https://ajax.googleapis.com/ajax/libs/jquery/1.7.2/jquery.min.js',
  juspay: `${environment.juspay}pay.js`,
  lg_hash: '/assets/js/lg-hash.min.js',
  lg_thumbnail: '/assets/js/lg-thumbnail.min.js',
  lg_zoom: '/assets/js/lg-zoom.min.js',
  lightgallery: 'https://cdnjs.cloudflare.com/ajax/libs/lightgallery-js/1.4.0/js/lightgallery.min.js',
  microsoft_clarity: microsoft_clarity,
  pusher: 'https://cdnjs.cloudflare.com/ajax/libs/pusher/4.3.1/pusher.min.js',
  razorpay: 'https://checkout.razorpay.com/v1/razorpay.js',
  stripeV2: 'https://js.stripe.com/v2/',
  stripeV3: 'https://js.stripe.com/v3/',
  verloop: verloop_script,
  zoho_internal: 'assets/js/zoho.js',
  vwo: 'assets/js/vwo.js',
  facebooksdk: 'https://connect.facebook.net/en_US/all.js'
} as const;

type ScriptKey = keyof typeof Scripts;






interface IScripts {
  name: string;
  src: string;
}
export const ScriptStore: IScripts[] = [
  {
    name: 'facebooksdk',
    src: 'https://connect.facebook.net/en_US/all.js'
  },
  {
    name: 'intl_tel_input',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/14.0.7/js/intlTelInput.min.js'
  },
  {
    name: 'intl_tel_input_util',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/14.0.7/js/utils.js'
  },
  {
    name: 'verloop',
    src: verloop_script
  },
  {
    name: 'zoho_internal',
    src: 'assets/js/zoho.js'
  },
  {
    name: 'stripe',
    src: 'https://js.stripe.com/v2/'
  },
  {
    name: 'jquery',
    src: 'https://ajax.googleapis.com/ajax/libs/jquery/1.7.2/jquery.min.js'
  },
  {
    name: 'juspay',
    src: `${environment.juspay}pay.js`
  },
  {
    name: 'pusher',
    src: `https://cdnjs.cloudflare.com/ajax/libs/pusher/4.3.1/pusher.min.js`
  },
  {
    name: 'twitter',
    src: `https://platform.twitter.com/widgets.js`
  },
  {
    name: 'vwo',
    src: 'assets/js/vwo.js'
  },
  {
    name: 'gtm',
    src: gtm_script
  },
  ,
  {
    name: 'clevertap',
    src: clevertap_script
  },
  {
    name: 'razor_pay',
    src: 'https://checkout.razorpay.com/v1/checkout.js'
  },
  {
    name: 'razor_pay_card',
    src: 'https://checkout.razorpay.com/v1/razorpay.js'
  },
  {
    name: 'razor_pay_custom_checkout',
    src: 'https://checkout.razorpay.com/v1/razorpay.js'
  },
  {
    name: 'fb_pixel_script',
    src: fb_pixel
  },
  {
    name: 'card-validator',
    src: '/assets/js/card-validator.js'
  },
  {
    name: 'blitzlamaScript',
    src: blitzlamaScript
  },
  {
    name: 'google_login',
    src: 'https://accounts.google.com/gsi/client'
  },
  {
    name: 'microsoft_clarity',
    src: microsoft_clarity
  }
];

@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {

  readonly vars = inject(VariablesService);

  /** Cache for loaded scripts */
  private loaded = new Set<ScriptKey>();

  /** Cache for in-flight promises (IMPORTANT) */
  private loading = new Map<ScriptKey, Promise<boolean>>();

  newLoad(key: ScriptKey, options?: { async?: boolean; defer?: boolean }): Promise<boolean> {
    if (!this.vars.isBrowser) {
      return Promise.resolve(false);
    }

    const src = Scripts[key];

    // already loaded
    if (this.loaded.has(key)) {
      this.loaded.add(key);
      return Promise.resolve(true);
    }

    // already loading
    if (this.loading.has(key)) {
      return this.loading.get(key)!;
    }

    const existingScript = this.getScript(src);
    if (existingScript) {
      const readyState = (existingScript as any).readyState;
      if (existingScript.dataset.loaded === 'true' || readyState === 'loaded' || readyState === 'complete') {
        this.loaded.add(key);
        return Promise.resolve(true);
      }

      const pendingScript = new Promise<boolean>((resolve, reject) => {
        existingScript.addEventListener('load', () => {
          existingScript.dataset.loaded = 'true';
          this.loaded.add(key);
          this.loading.delete(key);
          resolve(true);
        }, { once: true });
        existingScript.addEventListener('error', () => {
          this.loading.delete(key);
          existingScript.remove();
          reject(new Error(`Failed to load script: ${key}`));
        }, { once: true });
      });

      this.loading.set(key, pendingScript);
      return pendingScript;
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      const script = this.vars.document.createElement('script');

      script.src = src;
      script.async = options?.async ?? true;
      script.defer = options?.defer ?? true;
      script.dataset.scriptKey = key;

      script.onload = () => {
        script.dataset.loaded = 'true';
        this.loaded.add(key);
        this.loading.delete(key);
        resolve(true);
      };

      script.onerror = () => {
        this.loading.delete(key);
        // drop the failed tag, otherwise a retry finds it via getScript() and waits for events that never fire
        script.remove();
        reject(new Error(`Failed to load script: ${key}`));
      };

      this.vars.document.head.appendChild(script);
    });

    this.loading.set(key, promise);
    return promise;
  }

  private getScript(src: string): HTMLScriptElement | null {
    return this.vars.document.querySelector(`script[src="${src}"]`);
  }

  loadMany(keys: ScriptKey[]): Promise<boolean[]> {
    return Promise.all(keys.map(k => this.newLoad(k)));
  }

  removeScript(key: ScriptKey): void {
    if (!this.vars.isBrowser) return;

    const src = Scripts[key];

    // Remove from DOM
    const scripts = this.vars.document.querySelectorAll<HTMLScriptElement>(`script[src="${src}"]`);
    scripts.forEach(script => script.remove());

    // Cleanup caches
    this.loaded.delete(key);
    this.loading.delete(key);
  }






  private scripts: any = {};

  constructor(
  ) {
    ScriptStore.forEach((script: any) => {
      this.scripts[script.name] = {
        loaded: false,
        src: script.src
      };
    });
  }

  load(...scripts: string[]) {
    const promises: any[] = [];
    scripts.forEach((script) => promises.push(this.loadScript(script)));
    return Promise.all(promises);
  }

  loadScript(name: string, params?: any, full_script?: true) {
    return new Promise((resolve, reject) => {
      try {
        // resolve if already loaded
        if (this.scripts[name].loaded) {
          resolve({ script: name, loaded: true, status: 'Already Loaded' });
        } else {
          // load script
          const script: any = this.vars.document.createElement('script');
          script.type = 'text/javascript';
          script.setAttribute('rel', 'preconnect');
          full_script ? script.innerHTML = this.scripts[name].src : script.src = this.scripts[name].src;
          // script.async = true;
          script.defer = true;
          if (params) {
            for (const key in params) {
              if (params.hasOwnProperty(key)) {
                script.setAttribute(key, params[key]);
              }
            }
          }
          if (script.readyState) {  // IE
            script.onreadystatechange = () => {
              if (script.readyState === 'loaded' || script.readyState === 'complete') {
                script.onreadystatechange = null;
                this.scripts[name].loaded = true;
                resolve({ script: name, loaded: true, status: 'Loaded' });
              }
            };
          } else {  // Others
            script.onload = () => {
              this.scripts[name].loaded = true;
              resolve({ script: name, loaded: true, status: 'Loaded' });
            };
          }
          script.onerror = (error: any) => resolve({ script: name, loaded: false, status: 'Loaded' });
          this.vars.document.getElementsByTagName('head')[0].appendChild(script);
        }
      } catch (error) {
        reject(false);
      }
    });
  }

}
